package com.evoapps.evoshift.location

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.model.LocationReading

/**
 * SH-106: reads the device's position once, at the clock button's tap, asking for the permission
 * then if needed (Android: fine or coarse location; iOS: when in use; web: the browser's prompt).
 * Nothing runs in the background and nothing is kept on the device.
 */
typealias LocationReader = suspend () -> LocationReading

/** Replaces the platform reader (UI tests: a denied permission, a position far away). */
val LocalLocationReader = staticCompositionLocalOf<LocationReader?> { null }

/** The reader for a screen with a clock button. */
@Composable
fun rememberLocationReader(): LocationReader = LocalLocationReader.current ?: rememberPlatformLocationReader()

@Composable
expect fun rememberPlatformLocationReader(): LocationReader

/** How long a reading may take before it counts as no fix. */
internal const val LOCATION_TIMEOUT_MILLIS = 15_000L

/**
 * Whether this device already showed the person why the location is read (GDPR information, once
 * per device, before the first reading).
 */
interface LocationNoticeStore {
    fun isAcknowledged(): Boolean
    fun acknowledge()
}

class InMemoryLocationNoticeStore(private var acknowledged: Boolean = false) : LocationNoticeStore {
    override fun isAcknowledged(): Boolean = acknowledged
    override fun acknowledge() {
        acknowledged = true
    }
}

expect fun platformLocationNoticeStore(): LocationNoticeStore

internal const val LOCATION_NOTICE_KEY = "evoshift.locationNoticeSeen"
